
import { useState, useRef, useEffect } from 'react';

interface ProtectionTier {
  name: string;
  description: string;
  features: string[];
  icon: string;
  color: string;
}

export default function EndpointProtection() {
  const [visibleItems, setVisibleItems] = useState<number[]>([]);
  const [activeTier, setActiveTier] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  const protectionTiers: ProtectionTier[] = [
    {
      name: "Antivirus Protection",
      description: "Traditional signature-based malware detection and removal",
      features: ["Real-time Scanning", "Quarantine Management", "Automatic Updates", "Scheduled Scans", "Malware Removal"],
      icon: "ri-bug-line",
      color: "from-green-600 to-emerald-500"
    },
    {
      name: "EDR (Endpoint Detection & Response)",
      description: "Advanced behavioral analysis and automated threat response",
      features: ["Behavioral Analysis", "Threat Hunting", "Incident Response", "Forensic Investigation", "Remote Remediation"],
      icon: "ri-search-eye-line",
      color: "from-blue-600 to-cyan-500"
    },
    {
      name: "XDR (Extended Detection & Response)",
      description: "Unified security across endpoints, networks, and cloud environments",
      features: ["Cross-Platform Visibility", "Automated Correlation", "Advanced Analytics", "Threat Intelligence", "Unified Dashboard"],
      icon: "ri-radar-line",
      color: "from-purple-600 to-pink-500"
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            setVisibleItems(prev => [...prev, index]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const items = sectionRef.current?.querySelectorAll('.protection-item');
    items?.forEach(item => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-4 sm:px-6 lg:px-8 relative">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-transparent">
              Endpoint Protection
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Comprehensive endpoint security solutions from traditional antivirus to advanced EDR and XDR platforms
          </p>
        </div>

        {/* Interactive Protection Tiers */}
        <div className="mb-16">
          <div className="flex flex-col lg:flex-row justify-center mb-8 space-y-4 lg:space-y-0 lg:space-x-4">
            {protectionTiers.map((tier, index) => (
              <button
                key={index}
                onClick={() => setActiveTier(index)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap transform hover:scale-105 ${
                  activeTier === index
                    ? `bg-gradient-to-r ${tier.color} text-white shadow-lg`
                    : 'bg-slate-800/60 text-gray-300 hover:bg-slate-700/60 border border-slate-600'
                }`}
              >
                <i className={`${tier.icon} mr-2`}></i>
                {tier.name}
              </button>
            ))}
          </div>

          {/* Active Tier Details */}
          <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-r ${protectionTiers[activeTier].color} rounded-xl flex items-center justify-center mr-4`}>
                    <i className={`${protectionTiers[activeTier].icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white">
                    {protectionTiers[activeTier].name}
                  </h3>
                </div>
                
                <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                  {protectionTiers[activeTier].description}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {protectionTiers[activeTier].features.map((feature, featureIndex) => (
                    <div 
                      key={featureIndex}
                      className="flex items-center p-3 bg-slate-700/40 rounded-lg border border-slate-600/50 hover:border-green-400/50 transition-colors duration-300"
                    >
                      <i className="ri-check-line text-green-400 mr-3"></i>
                      <span className="text-gray-200">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <img 
                  src={`https://readdy.ai/api/search-image?query=3D%20rendered%20cybersecurity%20endpoint%20protection%20visualization%20showing%20$%7BprotectionTiers%5BactiveTier%5D.name.toLowerCase%28%29%7D%20with%20multiple%20connected%20devices%2C%20security%20shields%2C%20real-time%20threat%20detection%20interface%2C%20glowing%20security%20barriers%20around%20laptops%20and%20mobile%20devices%2C%20professional%20cybersecurity%20monitoring%20dashboard&width=600&height=500&seq=endpoint${activeTier}&orientation=landscape`}
                  alt={protectionTiers[activeTier].name}
                  className="w-full h-auto rounded-2xl shadow-2xl hover:scale-105 transform transition-transform duration-700"
                />
                
                {/* Floating 3D security badge */}
                <div className={`absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-r ${protectionTiers[activeTier].color} rounded-full flex items-center justify-center animate-pulse shadow-lg`}>
                  <i className={`${protectionTiers[activeTier].icon} text-white text-xl`}></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Security Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {[
            { label: "Threats Blocked", value: "99.7%", icon: "ri-shield-check-line", color: "from-green-500 to-emerald-400" },
            { label: "Detection Speed", value: "< 10ms", icon: "ri-flashlight-line", color: "from-blue-500 to-cyan-400" },
            { label: "False Positives", value: "< 0.1%", icon: "ri-error-warning-line", color: "from-purple-500 to-pink-400" },
            { label: "Endpoint Coverage", value: "100%", icon: "ri-computer-line", color: "from-orange-500 to-red-400" }
          ].map((stat, index) => (
            <div
              key={index}
              data-index={index}
              className={`protection-item bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 text-center hover:bg-slate-800/60 transform transition-all duration-500 hover:scale-105 cursor-pointer ${
                visibleItems.includes(index) ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-r ${stat.color} rounded-xl flex items-center justify-center`}>
                <i className={`${stat.icon} text-2xl text-white`}></i>
              </div>
              <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
              <div className="text-gray-400">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* 3D Device Protection Visualization */}
        <div className="text-center">
          <div className="relative inline-block group">
            <img 
              src="https://readdy.ai/api/search-image?query=3D%20rendered%20endpoint%20security%20ecosystem%20showing%20multiple%20devices%20including%20laptops%2C%20smartphones%2C%20tablets%2C%20and%20servers%20all%20connected%20with%20glowing%20security%20barriers%2C%20holographic%20protection%20shields%2C%20cybersecurity%20network%20visualization%20with%20real-time%20threat%20monitoring%2C%20futuristic%20blue%20and%20green%20color%20scheme&width=1200&height=700&seq=devices3d&orientation=landscape"
              alt="3D Device Protection"
              className="max-w-full h-auto rounded-2xl shadow-2xl hover:scale-105 transform transition-all duration-700 cursor-pointer"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-green-600/20 to-cyan-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
